import { useContext, useEffect, useMemo, useState } from "react";
import { Combobox } from "@headlessui/react";
import { toast } from "react-hot-toast";
import {
  IconBrandDiscord,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconLink,
  IconPhoto,
  IconSelector,
  IconX,
} from "@tabler/icons-react";

import { BrandContext } from "../../../../contexts/BrandContext/context";
import { RichTextEditor } from "../../../../components/Editor/RichTextEditor";
import { ImageUploader } from "../../../../components/ImageUploader/ImageUploader";
import { nicheService } from "../../../../services/niche.service";
import { storageService } from "../../../../services/storage.service";
import { brandService } from "../../../../services/brand.service";
import { socialMediaService, SocialMedia } from "../../../../services/social-media.service";
import { Niche } from "../../../../types/niche";
import { getRichTextLimitError } from "@/utils/richText";
import { BrandPreview } from "../../../brand/BrandPreview";
import { extractSocialMediaUsername } from "../../../../utils/socialMediaUtils";
import { ImageCropModal } from "../../../../components/ImageCropModal/ImageCropModal";
import { readFile } from "../../../../utils/canvasUtils";
import { uploadBannerSet } from "../../../../utils/bannerImage";
import { BrandDetails } from "../../../../types/brand";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { IconButton } from "@/components/dashboard/icon-button";
import { ActionDialog, DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import {
  Field,
  SettingsGrid,
  SettingsSection,
  UnsavedBar,
  useSections,
} from "@/components/settings/settings-kit";
import { useDriver } from "@/contexts/driver";
import { cn } from "@/lib/utils";

interface CompanyFormData {
  companyName: string;
  username: string;
  niches: Niche[];
  description: string;
  metaTitle: string;
  metaDescription: string;
  socialLinks: { id: string; username: string }[];
  shortDiscountUrl: boolean;
}

interface SocialMediaData {
  id: string;
  username: string;
  socialMediaPlatformId: string;
  brandDetailId: string;
  socialMediaPlatform: { id: string; name: string; url: string; iconUrl: string; active: boolean };
}

interface ImagePreview {
  file: File | null;
  previewUrl: string;
}

const PLACEHOLDER = "/placeholder/no-image.jpg";
const META_TITLE_MAX = 60;
const META_DESCRIPTION_MAX = 160;

const SOCIAL_PLATFORMS: Array<SocialMedia["name"]> = [
  "INSTAGRAM",
  "TIKTOK",
  "YOUTUBE",
  "FACEBOOK",
  "DISCORD",
  "OTHER",
];

const SOCIAL_ICONS: Record<string, typeof IconBrandInstagram> = {
  INSTAGRAM: IconBrandInstagram,
  TIKTOK: IconBrandTiktok,
  YOUTUBE: IconBrandYoutube,
  FACEBOOK: IconBrandFacebook,
  DISCORD: IconBrandDiscord,
  OTHER: IconLink,
};

const SOCIAL_LABELS: Record<string, string> = {
  INSTAGRAM: "Instagram",
  TIKTOK: "TikTok",
  YOUTUBE: "YouTube",
  FACEBOOK: "Facebook",
  DISCORD: "Discord",
  OTHER: "Website",
};

function isPlaceholder(url: string) {
  return !url || url.includes("/placeholder/no-image");
}

function socialLinksOf(brand: BrandDetails | null | undefined) {
  return (
    (brand?.socialMedia as SocialMediaData[] | undefined)?.map((social) => ({
      id: social.socialMediaPlatform.id,
      username: social.username,
    })) || []
  );
}

/** A social link whose adornment is the platform's icon. */
function SocialField({
  platform,
  value,
  onChange,
  invalid,
}: {
  platform: SocialMedia;
  value: string;
  onChange: (input: string) => void;
  invalid?: boolean;
}) {
  const Icon = SOCIAL_ICONS[platform.name] ?? IconLink;
  const label = SOCIAL_LABELS[platform.name] ?? platform.name;
  const isDiscord = platform.name === "DISCORD";
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`social-${platform.id}`} className="text-foreground">
        {label}
      </Label>
      <div className="relative">
        <Icon
          size={16}
          stroke={1.75}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          id={`social-${platform.id}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isDiscord ? "https://discord.gg/your-server" : "username"}
          className={cn("pl-9", invalid && "border-destructive")}
          autoComplete="off"
        />
      </div>
      {invalid && <p className="text-xs text-destructive">Enter the full invite link, starting with https://</p>}
    </div>
  );
}

function CompanyDetailsSkeleton() {
  return (
    <SettingsGrid
      form={
        <>
          {[3, 2, 1].map((fields, i) => (
            <div key={i} className="rounded-lg border border-border bg-card">
              <div className="px-5 pt-4 pb-3 animate-pulse">
                <div className="h-4 w-28 rounded bg-muted" />
              </div>
              <div className="space-y-5 px-5 pb-5 animate-pulse">
                {Array.from({ length: fields }).map((_, j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-3.5 w-28 rounded bg-muted" />
                    <div className="h-9 w-full rounded-lg bg-muted" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      }
      aside={
        <div className="rounded-lg border border-border bg-card">
          <div className="px-5 pt-4 pb-3 animate-pulse">
            <div className="h-4 w-32 rounded bg-muted" />
          </div>
          <div className="animate-pulse">
            <div className="h-24 bg-muted" />
            <div className="flex items-center gap-3 px-5 py-4">
              <div className="size-12 rounded-full bg-muted" />
              <div className="space-y-2">
                <div className="h-4 w-32 rounded bg-muted" />
                <div className="h-3 w-24 rounded bg-muted" />
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}

export function CompanyDetails() {
  const { brandDetails, fetchBrandDetails } = useContext(BrandContext);
  const brand = brandDetails as BrandDetails | null;
  const { isTourOpen } = useDriver();
  const sections = useSections("company", isTourOpen);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [socialMedias, setSocialMedias] = useState<SocialMedia[]>([]);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState<CompanyFormData>({
    companyName: "",
    username: "",
    niches: [],
    description: "",
    metaTitle: "",
    metaDescription: "",
    socialLinks: [],
    shortDiscountUrl: false,
  });

  const [showShortUrlConfirm, setShowShortUrlConfirm] = useState(false);
  const [showReauthPrompt, setShowReauthPrompt] = useState(false);
  const needsShortUrlReauth = Boolean(brand?.needsShortUrlReauth);
  const shopDomain = brand?.shop as string | undefined;

  const handleReauthRedirect = () => {
    if (!shopDomain) return;
    const apiBase = import.meta.env.VITE_API_URL as string;
    window.location.href = `${apiBase}/offline/auth?shop=${encodeURIComponent(shopDomain)}`;
  };

  const [logoPreview, setLogoPreview] = useState<ImagePreview>({ file: null, previewUrl: PLACEHOLDER });
  // The banner as uploaded. The 5:1 / 2:1 strips the join page needs are
  // rendered from it on save; framing lives under Customizations.
  const [bannerPreview, setBannerPreview] = useState<ImagePreview>({ file: null, previewUrl: PLACEHOLDER });
  const [metaImagePreview, setMetaImagePreview] = useState<ImagePreview>({ file: null, previewUrl: PLACEHOLDER });

  const [showLogoCropModal, setShowLogoCropModal] = useState(false);
  const [tempLogoSrc, setTempLogoSrc] = useState("");
  const [tempLogoFile, setTempLogoFile] = useState<File | null>(null);
  const [showMetaImageCropModal, setShowMetaImageCropModal] = useState(false);
  const [tempMetaImageSrc, setTempMetaImageSrc] = useState("");
  const [tempMetaImageFile, setTempMetaImageFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchSocialMedias = async () => {
      try {
        const platforms = await socialMediaService.findAll();
        const available = platforms.map((p) => p.name);
        const missing = SOCIAL_PLATFORMS.filter((name) => !available.includes(name));
        const fallback: SocialMedia[] = missing.map((name) => ({
          id: `fallback-${name.toLowerCase()}`,
          name,
          url: "",
          iconUrl: "",
          active: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));
        setSocialMedias([...platforms, ...fallback]);
      } catch {
        toast.error("Could not load social platforms");
      }
    };
    fetchSocialMedias();
  }, []);

  const fromBrand = (): CompanyFormData => ({
    companyName: brand?.companyName || "",
    username: brand?.username || "",
    niches: brand?.niches || [],
    description: brand?.description || "",
    metaTitle: (brand?.metaTitle as string) || "",
    metaDescription: (brand?.metaDescription as string) || "",
    socialLinks: socialLinksOf(brand),
    shortDiscountUrl: (brand?.shortDiscountUrl as boolean | undefined) ?? false,
  });

  const resetImages = () => {
    for (const p of [logoPreview, bannerPreview, metaImagePreview]) {
      if (p.previewUrl.startsWith("blob:")) URL.revokeObjectURL(p.previewUrl);
    }
    setLogoPreview({ file: null, previewUrl: brand?.brandProfileImage || PLACEHOLDER });
    setBannerPreview({
      file: null,
      previewUrl: brand?.brandBannerImageOriginal || brand?.brandBannerImage || PLACEHOLDER,
    });
    setMetaImagePreview({ file: null, previewUrl: (brand?.metaImage as string) || PLACEHOLDER });
  };

  useEffect(() => {
    if (!brand) return;
    setIsInitialLoading(false);
    setFormData(fromBrand());
    resetImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandDetails]);

  const hasChanges = useMemo(() => {
    if (!brand) return false;
    const original = fromBrand();
    return (
      formData.companyName !== original.companyName ||
      formData.username !== original.username ||
      formData.description !== original.description ||
      formData.metaTitle !== original.metaTitle ||
      formData.metaDescription !== original.metaDescription ||
      formData.shortDiscountUrl !== original.shortDiscountUrl ||
      logoPreview.file !== null ||
      bannerPreview.file !== null ||
      metaImagePreview.file !== null ||
      JSON.stringify(formData.niches) !== JSON.stringify(original.niches) ||
      JSON.stringify(formData.socialLinks) !== JSON.stringify(original.socialLinks)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, brandDetails, logoPreview.file, bannerPreview.file, metaImagePreview.file]);

  const pickImage = async (file: File, setSrc: (s: string) => void, setFile: (f: File) => void, open: () => void) => {
    try {
      setFile(file);
      setSrc(await readFile(file));
      open();
    } catch (error) {
      console.error("Error reading file:", error);
      toast.error("Could not read that image");
    }
  };

  const handleLogoUpload = (file: File) =>
    pickImage(file, setTempLogoSrc, setTempLogoFile, () => setShowLogoCropModal(true));
  const handleMetaImageUpload = (file: File) =>
    pickImage(file, setTempMetaImageSrc, setTempMetaImageFile, () => setShowMetaImageCropModal(true));

  const handleBannerUpload = (file: File) => {
    if (bannerPreview.previewUrl.startsWith("blob:")) URL.revokeObjectURL(bannerPreview.previewUrl);
    setBannerPreview({ file, previewUrl: URL.createObjectURL(file) });
  };

  const handleLogoCropComplete = (croppedFile: File) => {
    if (logoPreview.previewUrl.startsWith("blob:")) URL.revokeObjectURL(logoPreview.previewUrl);
    setLogoPreview({ file: croppedFile, previewUrl: URL.createObjectURL(croppedFile) });
    setShowLogoCropModal(false);
    setTempLogoSrc("");
    setTempLogoFile(null);
  };

  const handleMetaImageCropComplete = (croppedFile: File) => {
    if (metaImagePreview.previewUrl.startsWith("blob:")) URL.revokeObjectURL(metaImagePreview.previewUrl);
    setMetaImagePreview({ file: croppedFile, previewUrl: URL.createObjectURL(croppedFile) });
    setShowMetaImageCropModal(false);
    setTempMetaImageSrc("");
    setTempMetaImageFile(null);
  };

  useEffect(() => {
    return () => {
      for (const url of [logoPreview.previewUrl, bannerPreview.previewUrl, metaImagePreview.previewUrl]) {
        if (url.startsWith("blob:")) URL.revokeObjectURL(url);
      }
    };
  }, [logoPreview.previewUrl, bannerPreview.previewUrl, metaImagePreview.previewUrl]);

  const [availableNiches, setAvailableNiches] = useState<Niche[]>([]);
  const [isLoadingNiches, setIsLoadingNiches] = useState(false);
  const [nicheQuery, setNicheQuery] = useState("");

  useEffect(() => {
    const fetchNiches = async () => {
      setIsLoadingNiches(true);
      try {
        setAvailableNiches(await nicheService.getAvailableNiches());
      } catch {
        toast.error("Could not load niches");
      } finally {
        setIsLoadingNiches(false);
      }
    };
    fetchNiches();
  }, []);

  const filteredNiches =
    nicheQuery === ""
      ? availableNiches
      : availableNiches.filter((niche) => niche.name.toLowerCase().includes(nicheQuery.toLowerCase()));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;

    const descriptionError = getRichTextLimitError(formData.description, 6000);
    if (descriptionError) {
      toast.error(descriptionError);
      return;
    }

    setIsSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        companyName: formData.companyName,
        username: formData.username,
        description: formData.description,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        niches: formData.niches.map((niche) => niche.id),
        socialLinks: formData.socialLinks,
        shortDiscountUrl: formData.shortDiscountUrl,
      };
      if (logoPreview.file) payload.brandProfileImage = await storageService.uploadCompanyLogo(logoPreview.file);
      if (bannerPreview.file) {
        // Original plus centre-cropped strips. A new image resets any
        // framing chosen for the previous one.
        Object.assign(payload, await uploadBannerSet(bannerPreview.file));
        payload.brandBannerCrop = null;
      }
      if (metaImagePreview.file) payload.metaImage = await storageService.uploadMetaImage(metaImagePreview.file);

      await brandService.updateCompanyDetails(payload);
      await fetchBrandDetails();
      toast.success("Company details saved");
    } catch (error) {
      console.error("Error updating company details:", error);
      toast.error("Could not save company details");
    } finally {
      setIsSaving(false);
    }
  };

  const reset = () => {
    setFormData(fromBrand());
    resetImages();
  };

  const joinLink = `${window.location.origin}/store/${formData.username || brand?.username || ""}/join`;
  const joinHost = window.location.host;

  const copyJoinLink = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      toast.success("Link copied");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const platforms = useMemo(
    () =>
      socialMedias
        .filter((p) => SOCIAL_PLATFORMS.includes(p.name))
        .filter((p, index, self) => self.findIndex((q) => q.name === p.name) === index)
        .sort((a, b) => SOCIAL_PLATFORMS.indexOf(a.name) - SOCIAL_PLATFORMS.indexOf(b.name)),
    [socialMedias]
  );

  const setSocial = (platform: SocialMedia, input: string) => {
    const username = input.startsWith("http") ? input : extractSocialMediaUsername(input);
    setFormData((prev) => ({
      ...prev,
      socialLinks: username
        ? [...prev.socialLinks.filter((l) => l.id !== platform.id), { id: platform.id, username }]
        : prev.socialLinks.filter((l) => l.id !== platform.id),
    }));
  };

  if (isInitialLoading) return <CompanyDetailsSkeleton />;

  const filledSocials = platforms.filter((p) => formData.socialLinks.some((l) => l.id === p.id && l.username));
  const nicheNames = formData.niches.map((n) => n.name).join(", ");
  const previewTitle = formData.metaTitle || `Join ${formData.companyName || "our"} affiliate program`;
  const previewDescription =
    formData.metaDescription || "Earn commission sharing products you love with your audience.";

  return (
    <form onSubmit={handleSubmit}>
      <SettingsGrid
        form={
          <>
            <SettingsSection
              id="company-section"
              title="Company"
              open={sections.isOpen("company")}
              onToggle={() => sections.toggle("company")}
              bodyClassName="space-y-5"
            >
              <Field id="CD-company-name" label="Company name" htmlFor="company-name">
                <Input
                  id="company-name"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  autoComplete="organization"
                />
              </Field>
              <Field
                id="CD-company-username"
                label="Username"
                htmlFor="company-username"
                help="Sets your join link. Changing it breaks links you have already shared."
              >
                <div className="flex items-center rounded-lg border border-input shadow-sm focus-within:ring-1 focus-within:ring-ring">
                  <span className="hidden shrink-0 select-none pl-3 text-sm text-muted-foreground sm:inline">
                    {joinHost}/store/
                  </span>
                  <Input
                    id="company-username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="border-0 shadow-none focus-visible:ring-0 sm:pl-0.5"
                    autoComplete="off"
                  />
                  <span className="shrink-0 select-none pr-3 text-sm text-muted-foreground">/join</span>
                </div>
              </Field>
              <Field id="CD-company-niches" label="Niches" help="What you sell, so the right affiliates find you">
                <Combobox
                  multiple
                  value={formData.niches}
                  onChange={(selected) => setFormData((prev) => ({ ...prev, niches: selected }))}
                >
                  <div className="relative">
                    <div className="flex h-9 items-center rounded-lg border border-input shadow-sm focus-within:ring-1 focus-within:ring-ring">
                      <Combobox.Input
                        className="h-full w-full bg-transparent px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                        onChange={(event) => setNicheQuery(event.target.value)}
                        displayValue={() => nicheQuery}
                        placeholder={formData.niches.length ? "Add another" : "Search niches"}
                      />
                      <Combobox.Button className="flex h-full items-center pr-2 text-muted-foreground">
                        <IconSelector size={16} stroke={1.75} aria-hidden />
                      </Combobox.Button>
                    </div>
                    <Combobox.Options className="absolute z-[10001] mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-popover py-1 text-sm shadow-md focus:outline-none">
                      {isLoadingNiches ? (
                        <div className="px-3 py-2 text-muted-foreground">Loading</div>
                      ) : filteredNiches.length === 0 ? (
                        <div className="px-3 py-2 text-muted-foreground">No niches match</div>
                      ) : (
                        filteredNiches.map((niche) => (
                          <Combobox.Option
                            key={niche.id}
                            value={niche}
                            className={({ active }) =>
                              cn(
                                "relative cursor-default select-none py-2 pl-8 pr-3 text-foreground",
                                active && "bg-accent"
                              )
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span className={cn("block truncate", selected && "font-medium")}>{niche.name}</span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 text-foreground">
                                    <IconCheck size={14} stroke={2} aria-hidden />
                                  </span>
                                )}
                              </>
                            )}
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
                {formData.niches.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {formData.niches.map((niche) => (
                      <span
                        key={niche.id}
                        className="inline-flex items-center gap-1 rounded-md bg-muted py-1 pl-2 pr-1 text-xs font-medium text-foreground"
                      >
                        {niche.name}
                        <button
                          type="button"
                          aria-label={`Remove ${niche.name}`}
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              niches: prev.niches.filter((n) => n.id !== niche.id),
                            }))
                          }
                          className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <IconX size={12} stroke={2} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </Field>
            </SettingsSection>

            <SettingsSection
              id="images-section"
              title="Logo and banner"
              open={sections.isOpen("images")}
              onToggle={() => sections.toggle("images")}
              bodyClassName="space-y-5"
            >
              <div id="CD-company-profile-photo" className="space-y-5">
                <Field label="Logo" help="Shown beside your name wherever affiliates see you">
                  <ImageUploader
                    id="company-logo-upload"
                    image={logoPreview.previewUrl}
                    onChange={handleLogoUpload}
                    showPreview={false}
                    helpText="Square, PNG, JPG or WebP, up to 3MB"
                  />
                </Field>
                <Field
                  label="Banner"
                  help="Tops your join page. Upload the full image; framing for desktop and mobile is set under Customizations."
                >
                  <ImageUploader
                    id="company-banner-upload"
                    image={bannerPreview.previewUrl}
                    onChange={handleBannerUpload}
                    showPreview={false}
                    helpText="Wide images work best, up to 3MB"
                  />
                </Field>
              </div>
            </SettingsSection>

            <SettingsSection
              id="description-section"
              title="Description"
              open={sections.isOpen("description")}
              onToggle={() => sections.toggle("description")}
            >
              <div id="CD-company-description">
                <RichTextEditor
                  content={formData.description}
                  onChange={(content) => setFormData({ ...formData, description: content })}
                  placeholder="Tell affiliates what you sell and who buys it"
                  maxLength={6000}
                />
              </div>
            </SettingsSection>

            <SettingsSection
              id="link-preview-section"
              title="Link preview"
              open={sections.isOpen("preview")}
              onToggle={() => sections.toggle("preview")}
              bodyClassName="space-y-5"
            >
              <p className="-mt-1 text-xs text-muted-foreground">
                What people see when your join link is shared in a chat or a post.
              </p>
              <Field
                label="Title"
                htmlFor="meta-title"
                trailing={`${formData.metaTitle.length} of ${META_TITLE_MAX}`}
              >
                <Input
                  id="meta-title"
                  value={formData.metaTitle}
                  onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value.slice(0, META_TITLE_MAX) })}
                  maxLength={META_TITLE_MAX}
                  placeholder={`Join ${formData.companyName || "our"} affiliate program`}
                />
              </Field>
              <Field
                label="Description"
                htmlFor="meta-description"
                trailing={`${formData.metaDescription.length} of ${META_DESCRIPTION_MAX}`}
              >
                <Textarea
                  id="meta-description"
                  value={formData.metaDescription}
                  onChange={(e) =>
                    setFormData({ ...formData, metaDescription: e.target.value.slice(0, META_DESCRIPTION_MAX) })
                  }
                  maxLength={META_DESCRIPTION_MAX}
                  placeholder="Earn commission sharing products you love with your audience."
                  className="resize-none"
                />
              </Field>
              <Field label="Image" help="1200 by 630 pixels, PNG, JPG or WebP, up to 3MB">
                <ImageUploader
                  id="meta-image-upload"
                  image={metaImagePreview.previewUrl}
                  onChange={handleMetaImageUpload}
                  showPreview={false}
                />
              </Field>
            </SettingsSection>

            <SettingsSection
              id="socials-section"
              title="Social profiles"
              open={sections.isOpen("socials")}
              onToggle={() => sections.toggle("socials")}
            >
              <div id="CD-company-social-links" className="grid gap-4 sm:grid-cols-2">
                {platforms.map((platform) => {
                  const value = formData.socialLinks.find((l) => l.id === platform.id)?.username || "";
                  const invalid = platform.name === "DISCORD" && Boolean(value) && !value.startsWith("http");
                  return (
                    <SocialField
                      key={platform.id}
                      platform={platform}
                      value={value}
                      onChange={(input) => setSocial(platform, input)}
                      invalid={invalid}
                    />
                  );
                })}
              </div>
            </SettingsSection>

            <SettingsSection
              id="referral-section"
              title="Referral links"
              open={sections.isOpen("referral")}
              onToggle={() => sections.toggle("referral")}
            >
              <div id="CD-short-discount-url" className="flex items-start justify-between gap-6">
                <div className="min-w-0 space-y-1">
                  <Label htmlFor="shortDiscountUrl" className="text-foreground">
                    Short discount links
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    New affiliates get <span className="text-foreground">store.com/CODE</span> instead of{" "}
                    <span className="text-foreground">store.com/discount/CODE</span>. Reveshare adds a Shopify
                    redirect for each one.
                  </p>
                  {needsShortUrlReauth && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Needs a new Shopify permission. Turn it on to reauthorize the app.
                    </p>
                  )}
                </div>
                <Switch
                  id="shortDiscountUrl"
                  checked={formData.shortDiscountUrl}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      setFormData((prev) => ({ ...prev, shortDiscountUrl: false }));
                    } else if (needsShortUrlReauth) {
                      setShowReauthPrompt(true);
                    } else {
                      setShowShortUrlConfirm(true);
                    }
                  }}
                />
              </div>
            </SettingsSection>
          </>
        }
        aside={
          <>
            {/* The one memorable thing on this tab: the brand as an
                affiliate meets it, redrawn from the form as it changes. */}
            <DashboardCard
              title="As affiliates see you"
              action={
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="inline-flex items-center gap-1 rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <IconExternalLink size={14} stroke={1.75} />
                  Full page
                </button>
              }
            >
              <div className="mx-5 mb-5 overflow-hidden rounded-md border border-border">
                <div className="relative h-24 bg-muted">
                  {!isPlaceholder(bannerPreview.previewUrl) && (
                    <img src={bannerPreview.previewUrl} alt="" className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="space-y-3 px-4 py-4">
                  <div className="flex items-center gap-3">
                    <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-card">
                      {isPlaceholder(logoPreview.previewUrl) ? (
                        <IconPhoto size={18} stroke={1.5} className="text-muted-foreground" />
                      ) : (
                        <img src={logoPreview.previewUrl} alt="" className="h-full w-full object-cover" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-[15px] font-semibold text-foreground">
                        {formData.companyName || "Company name"}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {nicheNames || "Add niches so affiliates can find you"}
                      </div>
                    </div>
                  </div>
                  {filledSocials.length > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      {filledSocials.map((p) => {
                        const Icon = SOCIAL_ICONS[p.name] ?? IconLink;
                        return <Icon key={p.id} size={16} stroke={1.75} aria-label={SOCIAL_LABELS[p.name]} />;
                      })}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
                    <span className="min-w-0 truncate text-xs text-muted-foreground" title={joinLink}>
                      {joinLink.replace(/^https?:\/\//, "")}
                    </span>
                    <IconButton
                      label={copied ? "Copied" : "Copy join link"}
                      icon={copied ? <IconCheck stroke={2} /> : <IconCopy stroke={1.75} />}
                      onClick={() => void copyJoinLink()}
                      className={cn(copied && "text-foreground")}
                    />
                  </div>
                </div>
              </div>
            </DashboardCard>

            <DashboardCard title="When the link is shared">
              <div className="mx-5 mb-5 overflow-hidden rounded-md border border-border">
                {!isPlaceholder(metaImagePreview.previewUrl) ? (
                  <div className="aspect-[1.91/1] bg-muted">
                    <img src={metaImagePreview.previewUrl} alt="" className="h-full w-full object-cover" />
                  </div>
                ) : (
                  <div className="flex h-24 items-center justify-center gap-2 bg-muted text-xs text-muted-foreground">
                    <IconPhoto size={16} stroke={1.5} />
                    No preview image yet
                  </div>
                )}
                <div className="space-y-0.5 border-t border-border bg-card px-3 py-2.5">
                  <div className="truncate text-[11px] text-muted-foreground">{joinHost}</div>
                  <div className="truncate text-sm font-semibold text-foreground">{previewTitle}</div>
                  <div className="line-clamp-2 text-xs text-muted-foreground">{previewDescription}</div>
                </div>
              </div>
            </DashboardCard>

            <UnsavedBar
              visible={hasChanges}
              saving={isSaving}
              onCancel={reset}
              saveId="CD-save-company-profile"
            />
          </>
        }
      />

      {showPreview && brand?.username && (
        <BrandPreview username={brand.username} onClose={() => setShowPreview(false)} />
      )}

      {showLogoCropModal && tempLogoSrc && tempLogoFile && (
        <ImageCropModal
          imageSrc={tempLogoSrc}
          onClose={() => {
            setShowLogoCropModal(false);
            setTempLogoSrc("");
            setTempLogoFile(null);
          }}
          onCropComplete={handleLogoCropComplete}
          originalFileName={tempLogoFile.name}
          aspectRatio={1}
          cropShape="round"
          title="Crop your logo"
          description="Drag to position, pinch or scroll to zoom"
        />
      )}

      {showMetaImageCropModal && tempMetaImageSrc && tempMetaImageFile && (
        <ImageCropModal
          imageSrc={tempMetaImageSrc}
          onClose={() => {
            setShowMetaImageCropModal(false);
            setTempMetaImageSrc("");
            setTempMetaImageFile(null);
          }}
          onCropComplete={handleMetaImageCropComplete}
          originalFileName={tempMetaImageFile.name}
          aspectRatio={1.91}
          cropShape="rect"
          title="Crop the preview image"
          description="Drag to position, pinch or scroll to zoom"
        />
      )}

      <ActionDialog
        open={showReauthPrompt}
        onClose={() => setShowReauthPrompt(false)}
        title="Reauthorize Reveshare"
        description="Short links need a Shopify permission your store has not granted yet."
        footer={
          <>
            <DialogTextButton onClick={() => setShowReauthPrompt(false)}>Cancel</DialogTextButton>
            <Button type="button" onClick={handleReauthRedirect}>
              Reauthorize
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          You will be sent to Shopify to grant it, then brought back here. Nothing else changes.
        </p>
      </ActionDialog>

      <ActionDialog
        open={showShortUrlConfirm}
        onClose={() => setShowShortUrlConfirm(false)}
        title="Turn on short discount links?"
        footer={
          <>
            <DialogTextButton onClick={() => setShowShortUrlConfirm(false)}>Cancel</DialogTextButton>
            <Button
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, shortDiscountUrl: true }));
                setShowShortUrlConfirm(false);
              }}
            >
              Turn on
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            Affiliates who join from now on get a link shaped like{" "}
            <span className="text-foreground">store.com/CODE</span>, backed by a Shopify redirect made for each of them.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Affiliates who already joined keep their current links.</li>
            <li>Store pages are untouched. Shopify only redirects addresses that would otherwise not exist.</li>
            <li>Applies to discount code programs only.</li>
          </ul>
          <DialogNotice tone="warning">Save changes afterwards to apply it.</DialogNotice>
        </div>
      </ActionDialog>
    </form>
  );
}
